import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Truck, Wheat } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { peso } from "../lib/api";
import { computeDeliveryBreakdown, FREE_DELIVERY_THRESHOLD } from "../lib/delivery";

export default function Cart() {
  const { items, update, remove, subtotal } = useCart();
  const nav = useNavigate();
  const breakdown = computeDeliveryBreakdown(items);
  const deliveryFee = breakdown.totalFee;
  const needed = Math.max(0, FREE_DELIVERY_THRESHOLD - breakdown.nonSunjinSubtotal);
  const progress = Math.min(100, (breakdown.nonSunjinSubtotal / FREE_DELIVERY_THRESHOLD) * 100);
  const hasNonSunjin = items.some((i) => i.category !== "Sunjin Feeds" && !i.variant_id);
  const hasSunjin = items.some((i) => i.category === "Sunjin Feeds" || i.variant_id);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center page-enter">
        <ShoppingBag className="h-14 w-14 text-[#2F5233] mx-auto" strokeWidth={1.5} />
        <h1 className="font-heading text-3xl font-bold text-[#1C2826] mt-4">Your cart is empty</h1>
        <p className="text-[#4A5D5A] mt-2">Browse fresh groceries, meats and condiments.</p>
        <Button className="mt-6 bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-full px-6" onClick={() => nav("/shop")} data-testid="empty-shop-btn">
          Start shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 page-enter">
      <div className="overline">Your order</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1C2826] tracking-tighter">Shopping Cart</h1>

      {/* Free delivery progress (only relevant if non-Sunjin items in cart) */}
      {hasNonSunjin && (
        <div
          className={`mt-6 rounded-2xl border p-4 sm:p-5 ${needed > 0 ? "bg-[#FFF6EE] border-[#FFD5B2]" : "bg-[#EAF5EC] border-[#BEDDC2]"}`}
          data-testid="free-delivery-bar"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${needed > 0 ? "bg-[#E67E22]/15 text-[#E67E22]" : "bg-[#2F5233]/15 text-[#2F5233]"}`}>
              <Truck className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="flex-1">
              {needed > 0 ? (
                <div className="text-sm text-[#1C2826]">
                  Add <span className="font-heading font-extrabold text-[#E67E22]" data-testid="amount-needed">{peso(needed)}</span> more in groceries / meats and get <span className="font-extrabold">FREE delivery</span> on those items.
                </div>
              ) : (
                <div className="text-sm text-[#1C2826] font-semibold" data-testid="free-delivery-unlocked">
                  🎉 Nice! You&apos;ve unlocked <span className="font-extrabold text-[#2F5233]">FREE delivery</span> on groceries / meats.
                </div>
              )}
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-white overflow-hidden border border-[#E1E5DF]">
            <div
              className={`h-full rounded-full transition-all duration-500 ${needed > 0 ? "bg-[#E67E22]" : "bg-[#2F5233]"}`}
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
          />
        </div>
      </div>
      )}

      {/* Sunjin delivery note */}
      {hasSunjin && (
        <div className="mt-4 rounded-2xl border border-[#E1E5DF] bg-[#F8F9F5] p-4 sm:p-5 flex items-start gap-3" data-testid="sunjin-delivery-note">
          <div className="h-10 w-10 rounded-full bg-[#2F5233]/10 text-[#2F5233] flex items-center justify-center flex-shrink-0">
            <Wheat className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="text-sm text-[#1C2826]">
            <span className="font-semibold">Sunjin Feeds delivery:</span>{" "}
            {breakdown.sunjinSackQty > 0 ? (
              <>
                ₱{10} × {breakdown.sunjinSackQty} sack{breakdown.sunjinSackQty > 1 ? "s" : ""}
                {breakdown.sunjinHasNonSack && <> · plus your fractional items ride free with the sack(s)</>}
                {" "}= <span className="font-extrabold text-[#2F5233]">{peso(breakdown.sunjinFee)}</span>
              </>
            ) : (
              <>flat <span className="font-extrabold text-[#2F5233]">₱20</span> for half-sack / per-kilo / pack orders</>
            )}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-6 sm:mt-8">
        <div className="md:col-span-2 space-y-3">
          {items.map((i) => (
            <div key={i.key || i.product_id} className="bg-white border border-[#E1E5DF] rounded-2xl p-3 sm:p-4 flex gap-3 sm:gap-4 items-center" data-testid={`cart-item-${i.key || i.product_id}`}>
              <img src={i.image_url} alt={i.name} className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl object-cover bg-[#F1F3EE] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <Link to={`/product/${i.product_id}`} className="font-heading font-semibold text-sm sm:text-base text-[#1C2826] hover:text-[#2F5233] line-clamp-2">{i.name}</Link>
                {i.variant_label && (
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider font-bold text-[#2F5233] mt-0.5">{i.variant_label}</div>
                )}
                <div className="text-xs sm:text-sm text-[#738380]">{peso(i.price)} / {i.unit}</div>
                <div className="mt-2 flex items-center gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center border border-[#E1E5DF] rounded-full">
                    <button className="h-8 w-8 flex items-center justify-center" onClick={() => update(i.key || i.product_id, i.quantity - 1)} data-testid={`cart-minus-${i.key || i.product_id}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <div className="w-8 text-center text-sm font-semibold">{i.quantity}</div>
                    <button className="h-8 w-8 flex items-center justify-center" onClick={() => update(i.key || i.product_id, i.quantity + 1)} data-testid={`cart-plus-${i.key || i.product_id}`}>
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <button onClick={() => remove(i.key || i.product_id)} className="text-[#E74C3C] hover:underline text-xs sm:text-sm flex items-center gap-1" data-testid={`cart-remove-${i.key || i.product_id}`}>
                    <Trash2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Remove</span>
                  </button>
                  <div className="sm:hidden ml-auto font-heading font-extrabold text-[#2F5233] text-sm" data-testid={`cart-line-total-m-${i.key || i.product_id}`}>{peso(i.price * i.quantity)}</div>
                </div>
              </div>
              <div className="hidden sm:block font-heading font-extrabold text-[#2F5233]" data-testid={`cart-line-total-${i.key || i.product_id}`}>{peso(i.price * i.quantity)}</div>
            </div>
          ))}
        </div>

        <div className="md:col-span-1">
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-5 sm:p-6 md:sticky md:top-24" data-testid="cart-summary">
            <h2 className="font-heading font-bold text-lg text-[#1C2826]">Order Summary</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-semibold" data-testid="cart-subtotal">{peso(subtotal)}</span></div>
              <div className="flex justify-between"><span>Delivery fee</span><span className="font-semibold" data-testid="cart-delivery-fee">{peso(deliveryFee)}</span></div>
              {hasNonSunjin && breakdown.nonSunjinFee === 0 && breakdown.nonSunjinSubtotal >= FREE_DELIVERY_THRESHOLD && (
                <div className="text-xs text-[#2F5233]">✓ Free delivery on groceries / meats (over ₱1,000)</div>
              )}
              {!hasNonSunjin && !hasSunjin && (
                <div className="text-xs text-[#738380]">Free delivery on groceries / meats over ₱1,000</div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-[#E1E5DF] flex justify-between items-center">
              <span className="font-heading font-semibold text-[#1C2826]">Total</span>
              <span className="font-heading font-extrabold text-2xl text-[#2F5233]" data-testid="cart-total">{peso(subtotal + deliveryFee)}</span>
            </div>
            <Button className="w-full mt-5 h-11 bg-[#E67E22] hover:bg-[#D35400] text-white rounded-full font-semibold" onClick={() => nav("/checkout")} data-testid="checkout-btn">
              Proceed to Checkout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
